import type { EstadoDS } from "@/components/ds";
import type { CapacitacionesDocumentoSlot } from "@/types/capacitaciones-documento";

/**
 * Pliegue del catálogo de documentos de empresa.
 *
 * Vive acá y no dentro de una pantalla porque lo consumen DOS: el panel de
 * Documentos Empresa (sus tarjetas resumen y los contadores del segmented) y
 * el Dashboard (el KPI "Documentos al día" y tres de las filas de "Requiere tu
 * atención"). Regla #1 del spec — "un dato, un número": si cada pantalla
 * plegara por su cuenta, tarde o temprano una diría 27/32 y la otra 28/32, y
 * no habría forma de saber cuál miente.
 */

/**
 * Estado canónico del DS para un slot — deriva de "No aplica", presencia de
 * archivo y vigencia, EN ESE ORDEN. Un documento marcado "No aplica" no está
 * pendiente ni vencido: está fuera del avance.
 */
export function estadoDocumentoDs(slot: CapacitacionesDocumentoSlot): EstadoDS {
  if (slot.no_aplica) return "no_aplica";
  if (!slot.documento) return "pendiente";
  if (slot.documento.estado_vencimiento === "vencido") return "vencido";
  if (slot.documento.estado_vencimiento === "por_vencer") return "por_vencer";
  return "subido";
}

export interface ResumenDocumentosEmpresa {
  /** Documentos del catálogo que SÍ le aplican a la empresa. Denominador del avance. */
  aplicables: number;
  /** Marcados "No aplica": fuera del denominador y sin alertas. */
  nNA: number;
  /** Aplicables con archivo subido, en cualquier estado de vigencia. */
  cargados: number;
  /** Aplicables sin archivo. Es el número del badge del sidebar. */
  faltantes: number;
  pendientes: number;
  subidos: number;
  porVencer: number;
  vencidos: number;
  /** Documentos marcados como fuente de capacitación. */
  nCapActivas: number;
}

/**
 * Todos los contadores EXCLUYEN los marcados "No aplica" (salvo `nNA`, que los
 * cuenta, y `nCapActivas`, que mira el catálogo completo).
 */
export function resumirDocumentosEmpresa(
  slots: CapacitacionesDocumentoSlot[],
): ResumenDocumentosEmpresa {
  const aplicables = slots.filter((s) => !s.no_aplica);
  const cuenta = (estado: EstadoDS) =>
    aplicables.filter((s) => estadoDocumentoDs(s) === estado).length;
  const cargados = aplicables.filter((s) => s.documento !== null).length;

  return {
    aplicables: aplicables.length,
    nNA: slots.length - aplicables.length,
    cargados,
    faltantes: aplicables.length - cargados,
    pendientes: cuenta("pendiente"),
    subidos: cuenta("subido"),
    porVencer: cuenta("por_vencer"),
    vencidos: cuenta("vencido"),
    nCapActivas: slots.filter((s) => s.documento?.usar_para_capacitaciones).length,
  };
}
